// The whiteboard as a video track, without the browser's screen picker.
//
// Students only ever saw the board because the teacher screen-shared the tab it was drawn
// on: getDisplayMedia, a picker, and the teacher having to find their own window inside it.
// But the board is not a screen. It is a stack of <canvas> elements this app draws itself,
// and a canvas hands out a MediaStream directly — so the layers are composited into one
// offscreen canvas on a timer and captureStream() takes it from there. Same video track the
// producer wants, no permission prompt, and nothing of the teacher's desktop on the wire.
//
// Addressed by id rather than by ref on purpose: the layers live four components down
// (index.js -> PassiveCanvas -> SketchCanvasPro -> useCanvas), two of them class components
// with imperative handles, and they are already reached by id elsewhere in this codebase —
// passiveCanvas.js reads 'broadcast-canvas-get-image' exactly this way.

// Painted bottom to top, matching the z-index each layer is rendered at in
// components/sketchCanvasPro/index.js. The two export scratch canvases
// ('broadcast-canvas-download-overlay', 'broadcast-canvas-get-image') are not board content
// and are left out; 'broadcast-canvas-download' is the move-mode preview, which is real
// board content while a shape is being dragged and hidden the rest of the time.
export const BOARD_LAYER_IDS = [
    'broadcast-canvas-pdf',      // z1 — the PDF page behind everything
    'broadcast-canvas-image',    // z2 — placed images
    'broadcast-canvas',          // z3 — committed strokes
    'broadcast-canvas-download', // z5 — move-mode preview, hidden the rest of the time
    'broadcast-canvas-overlay',  // z6 — the stroke currently under the pen
];

// The teacher's camera, drawn into the board the way the class used to see it.
//
// Under the old screen-share route the picture-in-picture camera reached students for free:
// it was part of the window being captured. Capturing the board alone would quietly drop it
// and leave a class watching a disembodied whiteboard, so it is composited in here instead —
// same corner, and deliberately not mirrored (see the note on selfieClass in index.js: the
// flip is a courtesy to the person looking at their own camera and reaches everybody else
// backwards).
//
// widthRatio is the class's share, and it is not the teacher's. Their own .bn-pip is a card
// on a screen they can drag around; this is a rectangle burned into the board every student
// is trying to read, and it does not move. At 0.22 it took the top right corner outright —
// on a phone, where the board is already small, a fifth of the width of the lesson was a
// head. It was then tried at 0.08, which is the other end: 102px is a thumbnail, and a face
// stops carrying anything at that size on the phones most of a class watches on. 0.13 — 166px
// of the 1280 frame — is where it landed: a head big enough to read, small enough that the
// board is plainly the subject and the camera is plainly a corner of it.
//
// The teacher's own view is untouched — this constant only ever reaches the published
// stream. It is also compiled into the publisher's bundle and read while painting, so a
// change here shows up for the class only after the teacher's tab has reloaded and started
// the board again; nothing the viewer does will pick it up.
const BOARD_PIP_SELECTOR = '.bn-pip video';
const PIP = { widthRatio: 0.13, margin: 24, radius: 12 };

const pipSource = () => {
    try {
        const video = document.querySelector(BOARD_PIP_SELECTOR);
        // readyState below HAVE_CURRENT_DATA has no frame to draw yet, and a video whose
        // track has been stopped keeps its element but reports no intrinsic size.
        if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
        return video;
    } catch (error) {
        return null;
    }
};

// 720p and 15fps. A board is text and line art: it needs the pixels far more than it needs
// the frames, which is the same trade screenEncodingsForCapture makes for a shared screen.
// The capture size is fixed rather than taken from the window so that resizing the browser
// mid-class does not change the producer's input geometry — every such change costs the
// RTMP bridge an ffmpeg scale-graph rebuild, which is what swapVideoToScreen's note is about.
export const BOARD_CAPTURE = {
    width: 1280,
    height: 720,
    fps: 15,
    background: '#ffffff',
};

// A layer worth painting: it exists, it has been sized, and it is not one of the ones
// SketchCanvasPro parks behind visibility:hidden. Drawing a hidden layer would paint its
// stale contents over the live board.
const isPaintable = (element) => {
    if (!element || !element.width || !element.height) return false;
    try {
        return window.getComputedStyle(element).visibility !== 'hidden';
    } catch (error) {
        return true;
    }
};

// True once the board is actually mounted and drawable. The publish path asks before
// committing to a board capture, so a board that is not up yet fails the click rather than
// putting a blank white rectangle on the wire.
export const isBoardReady = () => BOARD_LAYER_IDS.some((id) => isPaintable(document.getElementById(id)));

// Returns { stream, track, stop }. Call stop() when the track comes off the producer —
// roomClient keeps it in stillTracks so clearStillTrack() does that on every swap away.
export function createBoardStream(options = {}) {
    const { width, height, fps, background } = { ...BOARD_CAPTURE, ...options };
    // See the diagnostic in paintPip. Per capture, so restarting the board prints again.
    let pipDiagShown = false;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    const paint = () => {
        // The board's own background is white (see _drawDC in sketchCanvasPro), and the
        // layers are transparent above it — so without this the strokes would go out over
        // whatever was in the buffer last.
        context.fillStyle = background;
        context.fillRect(0, 0, width, height);
        const layers = BOARD_LAYER_IDS
            .map((id) => document.getElementById(id))
            .filter(isPaintable);
        if (!layers.length) {
            // No board up yet — the publish chain runs ahead of the mount on the way into a
            // class. The camera still belongs on the frame, so the class sees the teacher
            // rather than a blank white rectangle while the board arrives.
            paintPip();
            return;
        }
        // Every layer is sized alike by resizeWithZoom, so the first one up decides the
        // geometry the rest are drawn against. Letterboxed rather than stretched: the board
        // is the window's aspect ratio and the capture is 16:9, and squashing a diagram to
        // fit is worse than a margin either side of it.
        const source = layers[0];
        const scale = Math.min(width / source.width, height / source.height);
        const drawWidth = source.width * scale;
        const drawHeight = source.height * scale;
        const left = (width - drawWidth) / 2;
        const top = (height - drawHeight) / 2;
        layers.forEach((layer) => {
            try {
                context.drawImage(layer, left, top, drawWidth, drawHeight);
            } catch (error) {
                // A layer mid-resize has zero intrinsic size for a frame and throws. Skipping
                // it costs one frame of that layer; letting it through costs the whole paint.
            }
        });
        paintPip();
    };

    const paintPip = () => {
        const video = pipSource();
        if (!video) return;
        const pipWidth = Math.round(width * PIP.widthRatio);
        const pipHeight = Math.round(pipWidth * video.videoHeight / video.videoWidth);
        // TEMP DIAGNOSTIC (safe to remove). Once per capture, not per frame. The size of
        // this rectangle has been argued about from screenshots, and a screenshot cannot
        // tell a composite the publisher drew from a camera the viewer's own layout put
        // there — whereas this line is the publisher stating what it drew. A capture that
        // never prints it is a capture that never composited a camera at all, which is the
        // answer that matters: whatever is on screen then came from somewhere else.
        if (!pipDiagShown){
            pipDiagShown = true;
            console.log('[BN-DIAG board-pip]', {
                captureWidth: width, widthRatio: PIP.widthRatio, pipWidth, pipHeight,
                share: `${Math.round(PIP.widthRatio * 100)}% of frame width`,
            });
        }
        // Top right, always. This used to sit bottom right, which put the class's view of
        // the camera in a different corner from the teacher's own self-view (.bn-pip is
        // top:16 right:16) — and it is the wrong corner for a board besides: writing starts
        // at the top left and runs down, so the bottom of the frame is where the newest
        // strokes land and the top right is the part of a board that stays empty longest.
        // Fixed rather than following the teacher's dragged self-view: that card is theirs to
        // move around their own screen, and having it drag the class's frame with it would
        // make the camera wander over the board mid-lesson for everyone.
        const left = width - pipWidth - PIP.margin;
        const top = PIP.margin;
        context.save();
        try {
            // Rounded corners so it reads as the same inset tile the teacher is looking at,
            // rather than a rectangle pasted onto their board.
            context.beginPath();
            if (context.roundRect){
                context.roundRect(left, top, pipWidth, pipHeight, PIP.radius);
            } else {
                context.rect(left, top, pipWidth, pipHeight);
            }
            context.clip();
            context.drawImage(video, left, top, pipWidth, pipHeight);
        } catch (error) {
            // A frame that is not decodable yet throws rather than drawing nothing.
        }
        context.restore();
    };

    paint();
    // A canvas nothing draws to stops handing frames to its capture stream, and a board sits
    // still for most of a lesson — so it is repainted on a timer whether or not the teacher
    // is drawing, exactly as swapVideoToStill does for the avatar frame.
    const timer = setInterval(paint, Math.round(1000 / fps));
    const stream = canvas.captureStream(fps);
    const track = stream.getVideoTracks()[0];
    if (track) {
        // Read back by produce() on a reconnect (`existing`) and by _tuneVideoSender, both of
        // which have to know this is detail-first content rather than a camera.
        track.customType = 'board';
    }
    const stop = () => {
        clearInterval(timer);
        try { track?.stop(); } catch (error) {}
    };
    return { stream, track, timer, stop, paint };
}
